import axios from 'axios'

import { actions as authActions, getFreshIdToken, getFirebaseAuth } from '../redux/slices/authSlice'

let interceptorsInstalled = false
let refreshPromise = null

const attachBearer = (config, token) => {
  if (!token) return config
  config.headers = config.headers || {}
  config.headers.Authorization = `Bearer ${token}`
  return config
}

/**
 * Keep API calls on a fresh Firebase ID token.
 * - Request: attach live getIdToken() when a Firebase user exists
 * - Response 401: force-refresh once and retry
 * - Sign out ONLY when Firebase refresh itself fails (no user / session dead)
 *   A 401 after a successful refresh is treated as an API error, not a logout.
 */
export const setupAuthHttp = (store) => {
  if (interceptorsInstalled) return
  interceptorsInstalled = true

  axios.interceptors.request.use(async (config) => {
    const authState = store.getState()?.auth || {}
    const firebaseUser = getFirebaseAuth().currentUser

    // Prefer live Firebase token; fall back to Redux token from the last sign-in
    if (firebaseUser) {
      try {
        const token = await firebaseUser.getIdToken(false)
        if (token && token !== authState.token) {
          store.dispatch(authActions.tokenUpdated(token))
        }
        return attachBearer(config, token)
      } catch {
        // Fall through to Redux token
      }
    }

    if (authState.token) {
      return attachBearer(config, authState.token)
    }

    return config
  })

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status
      const original = error?.config

      if (status !== 401 || !original) {
        return Promise.reject(error)
      }

      // Already retried with a fresh token — surface the API 401, do not nuke the session.
      // (Backend authz / misconfig can 401 even with a valid Firebase JWT.)
      if (original.__authRetry) {
        return Promise.reject(error)
      }

      original.__authRetry = true

      try {
        if (!refreshPromise) {
          refreshPromise = (async () => {
            const token = await getFreshIdToken(true)
            store.dispatch(authActions.tokenUpdated(token))
            return token
          })().finally(() => {
            refreshPromise = null
          })
        }

        const token = await refreshPromise
        attachBearer(original, token)
        return axios(original)
      } catch (refreshErr) {
        // Firebase session is actually gone / unusable
        const code = refreshErr?.code || ''
        const deadSession =
          code === 'auth/no-current-user' ||
          String(code).startsWith('auth/') ||
          !getFirebaseAuth().currentUser

        if (deadSession && store.getState()?.auth?.isAuthenticated) {
          await store.dispatch(authActions.signOutUser())
        }
        return Promise.reject(refreshErr)
      }
    }
  )
}
